import { Injectable } from '@angular/core'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { BehaviorSubject, Observable, throwError } from 'rxjs'
import { AuthenticationService } from '../services/authentication.service'
import { Router } from '@angular/router'
import { catchError, filter, switchMap, take } from 'rxjs/operators'
import { UnauthorizedAlpacaCode } from '../consts/unauthorizedAlpacaCode'
import { IAuthResponseData } from '../interfaces/IAuthResponseData'
import { IAuthRefreshResponse } from '../interfaces/IAuthRefreshResponse'
import { ToastrService } from 'ngx-toastr'
import { EnvironmentsHelperService } from '../../../../../environments/core/services/environments-helper.service'
import { environment } from '../../../../../environments/environment'

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
	private _isRefreshing: boolean = false

	private _refreshTokenSubject$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)

	constructor(
		private _authenticationService: AuthenticationService,
		private _router: Router,
		private _toastrService: ToastrService,
		private _environmentsHelperService: EnvironmentsHelperService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const currentUser: IAuthResponseData | null = this._authenticationService.currentUserValue
		const isAuthorized: boolean = !!currentUser?.tokens?.access_token

		if (isAuthorized) {
			if (
				req.url.startsWith(this._environmentsHelperService.apiUrl) ||
				req.url.startsWith(this._environmentsHelperService.clientTradeUrl) ||
				req.url.startsWith(this._environmentsHelperService.authUrl)
			) {
				const isRefresh: boolean = this._isRefreshRequest(req)
				const isLogout: boolean = this._isLogoutRequest(req)

				const useRefreshToken: boolean = isRefresh || isLogout
				const token: string = useRefreshToken
					? currentUser.tokens.refresh_token
					: currentUser.tokens.access_token
				req = this._addApiAuthorization(req, token)
			} else if (!this._isEnvironmentsConfigRequest(req)) {
				console.error('Неизвестный URL', req.url)
			}
		}

		return next.handle(req).pipe(catchError((error: HttpErrorResponse) => this._errorHandler(error, req, next)))
	}

	private _addApiAuthorization(req: HttpRequest<any>, token: string): HttpRequest<any> {
		return req.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
			},
		})
	}

	// конфиг окружения лежит на своём хосте и токена не требует — это не «неизвестный URL»
	private _isEnvironmentsConfigRequest(req: HttpRequest<any>): boolean {
		return req.url.startsWith(environment.environmentsConfigsUrl)
	}

	private _isRefreshRequest(req: HttpRequest<any>): boolean {
		return req.url.endsWith('/api/v1/auth/refresh')
	}

	// Покрывает /auth/login и /auth/login/2fa — на обоих refresh при 401 не нужен
	private _isLoginRequest(req: HttpRequest<any>): boolean {
		return req.url.includes('/api/v1/auth/login')
	}

	private _isLogoutRequest(req: HttpRequest<any>): boolean {
		return req.url.endsWith('/api/v1/auth/logout')
	}

	private _errorHandler(
		error: HttpErrorResponse,
		req: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		// 403 — отказ по правам или неверный пароль, refresh не поможет, не обрабатываем здесь
		if (error.status === 401) {
			if (error?.error?.code === UnauthorizedAlpacaCode) {
				this._toastrService.error(error?.error?.message)
				this._router.navigate(['/general/users'])
				return throwError(error)
			}

			if (this._isLogoutRequest(req)) {
				return throwError(error)
			}

			if (this._isRefreshRequest(req) || this._isLoginRequest(req)) {
				if (this._authenticationService.currentUserValue) {
					this._toastrService.error('Сессия истекла, войдите снова')
					this._authenticationService.logout()
					this._router.navigate(['/auth/login'])
				}
				return throwError(error)
			}

			if (this._authenticationService.currentUserValue) {
				return this._refreshAndRetry(req, next)
			}
		}
		return throwError(error)
	}

	private _refreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this._isRefreshing) {
			return this._refreshTokenSubject$.pipe(
				filter((token: string | null): boolean => token !== null),
				take(1),
				switchMap((token: string) => next.handle(this._addApiAuthorization(req, token))),
			)
		}

		this._isRefreshing = true
		this._refreshTokenSubject$.next(null)

		return this._authenticationService.refreshToken().pipe(
			switchMap((tokens: IAuthRefreshResponse) => {
				this._isRefreshing = false
				this._refreshTokenSubject$.next(tokens.access_token)
				return next.handle(this._addApiAuthorization(req, tokens.access_token))
			}),
			catchError((refreshError: HttpErrorResponse) => {
				this._isRefreshing = false
				this._refreshTokenSubject$.next(null)
				if (this._authenticationService.currentUserValue) {
					this._toastrService.error('Сессия истекла, войдите снова')
					this._authenticationService.logout()
					this._router.navigate(['/auth/login'])
				}
				return throwError(refreshError)
			}),
		)
	}
}
